'use client'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { authClient } from '@/lib/auth-client'
import { cn } from '@/lib/utils'
import { useForm } from '@tanstack/react-form'
import posthog from 'posthog-js'
import { toast } from 'sonner'
import { z } from 'zod/v4'

const formSchema = z.object({
    email: z.email('Invalid email address'),
})

export function ForgotPasswordForm({ className, ...props }: React.ComponentProps<'div'>) {
    const form = useForm({
        defaultValues: {
            email: '',
        },
        validators: {
            onSubmit: formSchema,
        },
        onSubmit: async ({ value }) => {
            const save = async () => {
                const data = await authClient.requestPasswordReset({
                    email: value.email,
                    redirectTo: `${window.location.origin}/reset-password`,
                })
                if (data.error) {
                    await new Promise((resolve) => setTimeout(resolve, 500))
                    throw new Error(data.error.message)
                }
                posthog.capture('password reset requested')
                return data
            }

            toast.promise(save(), {
                loading: 'Sending reset link...',
                success: 'If the account exists, you will receive an email.',
                error: (err) => `Could not send reset link: ${err.message ?? 'Unknown error'}`,
            })
        },
    })

    return (
        <div className={cn('flex flex-col gap-6', className)} {...props}>
            <Card className="overflow-hidden p-0">
                <CardContent className="p-6 md:p-8">
                    <form
                        onSubmit={(e) => {
                            e.preventDefault()
                            form.handleSubmit()
                        }}
                    >
                        <FieldGroup>
                            <div className="flex flex-col gap-2 text-center">
                                <h1 className="text-2xl font-bold">Forgot password?</h1>
                                <p className="text-muted-foreground text-sm text-balance">
                                    Enter your email to receive a reset link.
                                </p>
                            </div>
                            <form.Field
                                name="email"
                                children={(field) => {
                                    const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid
                                    return (
                                        <Field data-invalid={isInvalid}>
                                            <FieldLabel htmlFor={field.name}>Email</FieldLabel>
                                            <Input
                                                id={field.name}
                                                name={field.name}
                                                value={field.state.value}
                                                onBlur={field.handleBlur}
                                                onChange={(e) => field.handleChange(e.target.value)}
                                                aria-invalid={isInvalid}
                                                placeholder="joe@revally.com"
                                                autoComplete="off"
                                            />
                                            {isInvalid && <FieldError errors={field.state.meta.errors} />}
                                        </Field>
                                    )
                                }}
                            />
                            <Field>
                                <Button type="submit">Send reset link</Button>
                            </Field>
                            <FieldDescription className="text-center">
                                Remembered your password? <a href="/login">Sign in</a>
                            </FieldDescription>
                        </FieldGroup>
                    </form>
                </CardContent>
            </Card>
        </div>
    )
}
